import { Stack, Typography } from '@mui/material'
import React from 'react'
import { formatStringDate } from '../../store/utils/dateUtils'

interface HistoryContentRowsProps {
  oldData: any
  newData: any
  isAddition: boolean
  isDeletion: boolean
  t: (key: string) => string
}

const HistoryContentRows: React.FC<HistoryContentRowsProps> = ({ oldData, newData, isAddition, isDeletion, t }) => {
  const isVacation = (oldData && oldData.vacation) || (newData && newData.vacation)

  if (isVacation) {
    return null
  }

  const actionType = isAddition ? 'addition' : isDeletion ? 'deletion' : 'modification'

  let contentRows: React.ReactNode[] = []

  switch (actionType) {
    case 'addition':
    case 'deletion': {
      const ignoreKeys = ['id', 'version', 'created_at', 'employee_id', 'from_import', 'modified_at', 'project_id']
      const displayData = isAddition ? newData : oldData

      contentRows = Object.entries(displayData)
        .filter(([key, value]) => {
          if (key === 'remote') return value === true
          return !ignoreKeys.includes(key)
        })
        .map(([key, value]) => {
          switch (key) {
            case 'remote':
              return (
                <Stack key={key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {t('page.modification.remote')}
                  </Typography>
                  <Typography variant="body2">{t('page.modification.yes')}</Typography>
                </Stack>
              )
            case 'description': {
              const displayValue =
                value == null || String(value).trim() === '' ? t('page.modification.noDescription') : String(value)

              return (
                <Stack key={key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {t('page.modification.description')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {displayValue}
                  </Typography>
                </Stack>
              )
            }
            default:
              return (
                <Stack key={key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {t(`page.modification.${key}`)}
                  </Typography>
                  <Typography variant="body2">
                    {key === 'date' ? formatStringDate(String(value)) : String(value)}
                  </Typography>
                </Stack>
              )
          }
        })
      break
    }
    case 'modification': {
      const differences: { key: string; old: any; new: any }[] = []
      const ignoreKeys = ['id', 'employee_id', 'version', 'created_at', 'modified_at', 'from_import', 'project_id']
      const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)])

      allKeys.forEach(key => {
        if (ignoreKeys.includes(key)) return
        if (key === 'description' && !newData[key] && !oldData[key]) return
        if (key === 'date') {
          differences.push({ key, old: oldData[key], new: newData[key] })
        } else if (oldData[key] !== newData[key]) {
          differences.push({ key, old: oldData[key], new: newData[key] })
        }
      })

      contentRows = differences.map(diff => {
        switch (diff.key) {
          case 'remote': {
            const oldRemote = diff.old === true ? t('page.modification.yes') : t('page.modification.no')
            const newRemote = diff.new === true ? t('page.modification.yes') : t('page.modification.no')

            return (
              <Stack key={diff.key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {t('page.modification.remote')}
                </Typography>
                <Typography variant="body2">
                  {oldRemote} → {newRemote}
                </Typography>
              </Stack>
            )
          }
          case 'description': {
            const oldDesc =
              diff.old == null || String(diff.old).trim() === ''
                ? t('page.modification.noDescription')
                : String(diff.old)

            const newDesc =
              diff.new == null || String(diff.new).trim() === ''
                ? t('page.modification.noDescription')
                : String(diff.new)

            return (
              <Stack key={diff.key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {t('page.modification.description')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {oldDesc} → {newDesc}
                </Typography>
              </Stack>
            )
          }
          case 'date': {
            let displayValue: string
            if (diff.old && diff.new) {
              displayValue = diff.old === diff.new ? String(diff.old) : `${String(diff.old)} → ${String(diff.new)}`
            } else {
              displayValue = String(diff.old ?? diff.new ?? '')
            }

            return (
              <Stack key={diff.key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {t('page.modification.date')}
                </Typography>
                <Typography variant="body2">{displayValue}</Typography>
              </Stack>
            )
          }
          default:
            return (
              <Stack key={diff.key} direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {t(`page.modification.${diff.key}`)}
                </Typography>
                <Typography variant="body2">
                  {String(diff.old)} → {String(diff.new)}
                </Typography>
              </Stack>
            )
        }
      })
      break
    }
  }
  return <>{contentRows}</>
}

export default HistoryContentRows
